import { createSelector } from '@reduxjs/toolkit';
import {
  EquipmentTypes,
  type IMinimizedProfession,
  type IMergedFloorView,
} from '@d-orca/shared/trusstor';
import { professionColor } from '@utils/professionColor';
import {
  selectFloors,
  selectSafetyEvents,
  selectProfessions,
  selectActiveEquipment,
  selectSelectedProfessionIds,
  selectSelectedEquipmentTypes,
} from './monitor.base.selectors';

/** A floor's display name: its nickname, else its short one, else its id. */
const floorLabelOf = (floor: IMergedFloorView): string =>
  (floor.floorNick as string | undefined) ?? (floor.shortFloorNick as string | undefined) ?? (floor.floorId as string);

/** A floor id's display name (one rule everywhere); an id the site doesn't have reads as itself. */
export const selectFloorLabelById = createSelector([selectFloors], (floors) => {
  const labels = new Map(floors.map((f) => [f.floorId as string, floorLabelOf(f)]));
  return (floorId: string) => labels.get(floorId) ?? floorId;
});

/**
 * Floor summary for FloorViewCard + MainView floor picker.
 * Workers count is sum of activeProfessions.activeTagsIds across the floor.
 * Alerts count is the number of safety events on this floor.
 * isSpecial reflects trusstor's isSpecialFloor flag.
 */
export const selectFloorSummaries = createSelector(
  [selectFloors, selectSafetyEvents],
  (floors, safetyEvents) => {
    const alertsByFloor = new Map<string, number>();
    for (const e of safetyEvents) {
      if (!e.floorId) continue;
      alertsByFloor.set(e.floorId, (alertsByFloor.get(e.floorId) ?? 0) + 1);
    }
    return floors.map((f) => {
      const floorId = f.floorId as string;
      return {
        floorId,
        label: floorLabelOf(f),
        workersCount: f.activeProfessions.reduce((sum, p) => sum + p.activeTagsIds.length, 0),
        alertsCount: alertsByFloor.get(floorId) ?? 0,
        isSpecial: f.isSpecialFloor === true,
      };
    });
  },
);

export const selectSpecialFloors = createSelector([selectFloors], (floors) =>
  floors.filter((f) => f.isSpecialFloor === true),
);

export type FloorChip = {
  professionId: string;
  /** Specific trade — used to look up the localized chip text via
   *  `getTradeLabel(tradeId, locale)` against trusstor's translation
   *  table at render time. */
  tradeId: string;
  /** Broad category fallback when the tradeId has no translation entry
   *  (e.g. "construction", "systems"). */
  tradeGroup?: string;
  color: string;
  count: number;
};

/**
 * Group a floor or area's `activeProfessions` into one entry per
 * `tradeId`, summing the underlying tag counts. Mirrors trusstor's
 * `FloorProfessionsEquipment` rendering: chips are keyed by trade
 * (the specific identifier — `fireProtection`, `insulation`, etc.) not
 * the broader `tradeGroup` ("systems", "construction"). Multiple
 * profession `_id`s sharing the same tradeId (a base profession +
 * variants with different specialties) collapse into one chip.
 *
 * `tradeGroup` keying was the original implementation; it caused chips
 * to render as "systems" for every profession in that group instead of
 * the specific trade name trusstor displays.
 *
 * Count-based so the Monitor replay (profession counts per frame) builds its
 * chips by the same rule as live.
 */
export function aggregateTradeChips(
  entries: ReadonlyArray<{ _id: string; tradeGroup?: string; tradeId?: string; count: number }>,
  profById: ReadonlyMap<string, { tradeGroup?: string; backgroundColor?: string | null; tradeId?: string }>,
): FloorChip[] {
  const byTradeId = new Map<string, FloorChip>();
  for (const p of entries) {
    if (p.count === 0) continue;
    const full = profById.get(p._id);
    const tradeId = full?.tradeId ?? p.tradeId;
    if (!tradeId) continue; // can't render a chip without a trade identifier
    const tradeGroup = full?.tradeGroup ?? p.tradeGroup;
    const color = professionColor(full?.backgroundColor);
    const existing = byTradeId.get(tradeId);
    if (existing) {
      existing.count += p.count;
    } else {
      byTradeId.set(tradeId, {
        professionId: p._id,
        tradeId,
        tradeGroup,
        color,
        count: p.count,
      });
    }
  }
  return Array.from(byTradeId.values()).sort((a, b) => b.count - a.count);
}

/** The live adapter: a profession's count is its active tags. */
function aggregateTradesByTradeId(
  activeProfessions: ReadonlyArray<{
    _id: string;
    tradeGroup?: string;
    tradeId?: string;
    activeTagsIds: string[];
  }>,
  profById: Map<string, { tradeGroup?: string; backgroundColor?: string; tradeId?: string }>,
): FloorChip[] {
  return aggregateTradeChips(
    activeProfessions.map((p) => ({ _id: p._id, tradeGroup: p.tradeGroup, tradeId: p.tradeId, count: p.activeTagsIds.length })),
    profById,
  );
}

/**
 * The floor-shape fields below (`isHoist`, `isMovingFloor`,
 * `matchingFixedFloorId`, `matchingFixedFloorNick`) ARE declared on the
 * shared schema (`shared/src/trusstor/floor.ts:60,80-85`), but
 * `floorViewSchema`'s `.passthrough()` adds an index signature that
 * widens the inferred field types after `Omit<IFloorView, 'areas'>`
 * resolves them — `f.matchingFixedFloorId` then types as `{}` instead
 * of `string | undefined`. Narrow once here so each call site stays
 * readable; if `.passthrough()` ever moves, drop this helper.
 */
type FloorSpecialFlags = {
  isHoist?: boolean;
  isMovingFloor?: boolean;
  matchingFixedFloorId?: string;
  matchingFixedFloorNick?: string;
};

function flags(floor: unknown): FloorSpecialFlags {
  return floor as FloorSpecialFlags;
}

export type FloorWithChips = {
  floorId: string;
  label: string;
  workersCount: number;
  alertsCount: number;
  isSpecial: boolean;
  /** Hoist/elevator floors are shown in their own UI section, not the building slabs. */
  isHoist: boolean;
  /** Moving floors (scaffolds, lifts) are tracked separately, not in the slabs view. */
  isMovingFloor: boolean;
  chips: FloorChip[];
};

export const selectFloorChips = createSelector(
  [selectFloors, selectSafetyEvents, selectProfessions, selectSelectedProfessionIds],
  (floors, safetyEvents, professions, selectedProfIds): FloorWithChips[] => {
    const profById = new Map(professions.map((p) => [p._id, p]));
    const alertsByFloor = new Map<string, number>();
    for (const e of safetyEvents) {
      if (!e.floorId) continue;
      alertsByFloor.set(e.floorId, (alertsByFloor.get(e.floorId) ?? 0) + 1);
    }
    // Empty selection set = no filter (show every active profession). Non-empty
    // = whitelist by profession `_id`. Mirrors trusstor's `visibleProfessions`
    // semantics from `professions.slice`.
    const profFilter = selectedProfIds.length > 0 ? new Set(selectedProfIds) : null;
    return floors.map((f) => {
      const floorId = f.floorId as string;

      const filteredActive = profFilter
        ? f.activeProfessions.filter((p) => profFilter.has(p._id))
        : f.activeProfessions;
      const chips: FloorChip[] = aggregateTradesByTradeId(filteredActive, profById);

      return {
        floorId,
        label: floorLabelOf(f),
        workersCount: f.activeProfessions.reduce((sum, p) => sum + p.activeTagsIds.length, 0),
        alertsCount: alertsByFloor.get(floorId) ?? 0,
        isSpecial: f.isSpecialFloor === true,
        isHoist: flags(f).isHoist === true,
        isMovingFloor: flags(f).isMovingFloor === true,
        chips,
      };
    });
  },
);

/**
 * Per-fixed-floor hoist summary for the side-rail hoist badge. Hoists are
 * stored as their own floor entries with `isHoist === true`; their
 * `matchingFixedFloorId` points to the fixed floor they currently serve.
 * Mirrors trusstor's `BuildingDetails.tsx:50-53` grouping.
 */
export type FloorHoistsSummary = {
  count: number;
  /** ids of the underlying hoist-floor records grouped here. */
  hoistFloorIds: string[];
  /** True if any hoist in the group is currently moving (drives the
   *  static-vs-moving icon variant in trusstor). */
  hasMoving: boolean;
};

/**
 * Combined hoist grouping: one `floors` walk produces both the per-fixed-floor
 * summary (count + hasMoving) consumed by the side-rail badge, AND the per-
 * fixed-floor record list consumed by the hoist hover tooltip. Mirrors
 * trusstor's `BuildingDetails:50-53` grouping by `matchingFixedFloorId` —
 * we factor the two reader shapes through a single intermediate so SSE
 * `floors` updates don't pay the walk twice. The two named selectors
 * (`selectHoistsByFixedFloorId`, `selectHoistFloorsByFixedFloorId`) are
 * thin pluckers that downstream consumers can keep importing as before.
 */
const selectHoistGroupsByFixedFloorId = createSelector([selectFloors], (floors) => {
  const summary = new Map<string, FloorHoistsSummary>();
  const records = new Map<string, IMergedFloorView<IMinimizedProfession>[]>();
  for (const f of floors) {
    const fl = flags(f);
    if (fl.isHoist !== true) continue;
    const fixedFloorId = fl.matchingFixedFloorId;
    if (!fixedFloorId) continue;
    const isMoving = fl.isMovingFloor === true;
    const existing = summary.get(fixedFloorId);
    if (existing) {
      existing.count += 1;
      existing.hoistFloorIds.push(f.floorId as string);
      existing.hasMoving = existing.hasMoving || isMoving;
      records.get(fixedFloorId)!.push(f);
    } else {
      summary.set(fixedFloorId, {
        count: 1,
        hoistFloorIds: [f.floorId as string],
        hasMoving: isMoving,
      });
      records.set(fixedFloorId, [f]);
    }
  }
  return { summary, records };
});

export const selectHoistsByFixedFloorId = createSelector(
  [selectHoistGroupsByFixedFloorId],
  (groups) => groups.summary,
);

/**
 * Per-fixed-floor hoist records — full `IMergedFloorView` shape so the
 * hover tooltip can read areaNick, `matchingFixedFloorChangeTimestamp`,
 * managers, and activeProfessions. Mirrors what trusstor's `HoistIcon`
 * reaches into via `IFloorData` (`HoistIcon.tsx:60-176`).
 */
export const selectHoistFloorsByFixedFloorId = createSelector(
  [selectHoistGroupsByFixedFloorId],
  (groups) => groups.records,
);

export type SpecialAreaCard = {
  floorId: string;
  name: string;
  workersCount: number;
  hasOperator: boolean;
  /** Operator name when one is present on the floor. */
  operatorName: string | null;
  alertsCount: number;
  equipmentCount: number;
  /**
   * Hoists / scaffolds / elevators: track which fixed floor the moving floor is currently
   * docked at (matchingFixedFloorNick). null for static special areas (lobby, pool, etc).
   */
  isMoving: boolean;
  currentFloorLabel: string | null;
  professionChips: FloorChip[];
};

export const selectSpecialAreaCards = createSelector(
  [
    selectSpecialFloors,
    selectProfessions,
    selectActiveEquipment,
    selectSafetyEvents,
    selectSelectedProfessionIds,
    selectSelectedEquipmentTypes,
  ],
  (floors, professions, activeEq, safetyEvents, selectedProfIds, selectedEqTypes): SpecialAreaCard[] => {
    const profById = new Map(professions.map((p) => [p._id, p]));
    const profFilter = selectedProfIds.length > 0 ? new Set(selectedProfIds) : null;
    const eqFilter = selectedEqTypes.length > 0 ? new Set(selectedEqTypes) : null;
    // Active equipment only — static is the project's catalogue, not a
    // location feed (see `selectEquipmentCountByFloorId`).
    const eqByFloor = new Map<string, number>();
    for (const e of activeEq) {
      const fid = e.floorId;
      if (!fid) continue;
      if (eqFilter && !eqFilter.has(e.type as EquipmentTypes)) continue;
      eqByFloor.set(fid, (eqByFloor.get(fid) ?? 0) + 1);
    }
    const alertsByFloor = new Map<string, number>();
    for (const e of safetyEvents) {
      if (!e.floorId) continue;
      alertsByFloor.set(e.floorId, (alertsByFloor.get(e.floorId) ?? 0) + 1);
    }
    return floors.map((f) => {
      const fid = f.floorId as string;
      const name = floorLabelOf(f);
      const filteredActive = profFilter
        ? f.activeProfessions.filter((p) => profFilter.has(p._id))
        : f.activeProfessions;
      const chips = aggregateTradesByTradeId(filteredActive, profById);
      const firstManager = f.managers[0] as { name?: string } | undefined;
      const fl = flags(f);
      const matchingFixedFloorNick = fl.matchingFixedFloorNick;
      const matchingFixedFloorId = fl.matchingFixedFloorId;
      const isMoving = fl.isMovingFloor === true || fl.isHoist === true;
      return {
        floorId: fid,
        name,
        workersCount: f.activeProfessions.reduce((s, p) => s + p.activeTagsIds.length, 0),
        hasOperator: f.managers.length > 0,
        operatorName: firstManager?.name ?? null,
        alertsCount: alertsByFloor.get(fid) ?? 0,
        equipmentCount: eqByFloor.get(fid) ?? 0,
        isMoving,
        currentFloorLabel: matchingFixedFloorNick ?? matchingFixedFloorId ?? null,
        professionChips: chips,
      };
    });
  },
);
